import { useEffect, useState, type ReactNode } from "react";
import { clearAuth, isAuthed, setAuth } from "../lib/auth";

// Wraps the app. Shows a login form until a credential is stored in
// localStorage; once stored, the credential rides on every API call via
// the Authorization header (lib/api.ts). A 401 from any call fires
// `elvis-auth-expired`, which drops us back to the form. This replaces
// the browser-native Basic Auth popup so the session survives restarts.
export default function LoginGate({ children }: { children: ReactNode }) {
  const [authed, setAuthed] = useState<boolean>(isAuthed());
  const [user, setUser] = useState("");
  const [pass, setPass] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [checking, setChecking] = useState(false);

  useEffect(() => {
    const onExpired = (): void => {
      setAuthed(false);
      setError("Session expired — please sign in again.");
    };
    window.addEventListener("elvis-auth-expired", onExpired);
    return () => window.removeEventListener("elvis-auth-expired", onExpired);
  }, []);

  const submit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setError(null);
    setChecking(true);
    // Verify by hitting a guarded endpoint with the new credential.
    const token = btoa(`${user}:${pass}`);
    try {
      const res = await fetch("/tradingview/admin/api/overview", {
        headers: { authorization: `Basic ${token}` },
      });
      if (res.status === 401) {
        setError("Wrong username or password.");
        setChecking(false);
        return;
      }
      if (!res.ok) {
        setError(`Server error (${res.status}). Try again.`);
        setChecking(false);
        return;
      }
      setAuth(user, pass);
      setAuthed(true);
    } catch (err) {
      setError(`Network error: ${(err as Error).message}`);
    } finally {
      setChecking(false);
    }
  };

  if (authed) return <>{children}</>;

  return (
    <div className="flex min-h-screen items-center justify-center bg-canvas-dark px-4">
      <form
        onSubmit={submit}
        className="w-full max-w-sm rounded-lg border border-hairline-dark bg-surface-elevated/40 p-6"
      >
        <div className="mb-1 flex items-baseline gap-2">
          <span className="text-lg font-bold tracking-wider text-primary">ELVIS</span>
          <span className="text-[10px] uppercase tracking-[0.2em] text-muted">admin</span>
        </div>
        <p className="mb-5 text-sm text-muted">Sign in to continue</p>

        <label className="mb-3 block">
          <span className="mb-1 block text-[11px] uppercase tracking-wider text-muted">
            Username
          </span>
          <input
            type="text"
            autoFocus
            autoComplete="username"
            value={user}
            onChange={(e) => setUser(e.target.value)}
            className="w-full rounded-md border border-hairline-dark bg-canvas-dark px-3 py-2 font-mono text-on-dark"
          />
        </label>
        <label className="mb-4 block">
          <span className="mb-1 block text-[11px] uppercase tracking-wider text-muted">
            Password
          </span>
          <input
            type="password"
            autoComplete="current-password"
            value={pass}
            onChange={(e) => setPass(e.target.value)}
            className="w-full rounded-md border border-hairline-dark bg-canvas-dark px-3 py-2 font-mono text-on-dark"
          />
        </label>

        {error ? (
          <div className="mb-4 rounded-md border border-trading-down/40 bg-trading-down/10 px-3 py-2 text-sm text-trading-down">
            {error}
          </div>
        ) : null}

        <button
          type="submit"
          className="btn-primary w-full"
          disabled={checking || !user || !pass}
        >
          {checking ? "Signing in…" : "Sign in"}
        </button>
      </form>
    </div>
  );
}

// Exposed so a future "Log out" button can drop the session.
export function logout(): void {
  clearAuth();
  window.dispatchEvent(new Event("elvis-auth-expired"));
}
